import React, { useEffect, useRef, useState } from "react";
import { v2cx } from "../theme/tokens";
import { useV2Theme } from "../theme/ThemeProvider";

export function V2OverflowMenuItem({
  onClick,
  disabled = false,
  danger = false,
  children,
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={v2cx(
        "w-full text-left px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed",
        danger
          ? "text-rose-700 hover:bg-rose-50"
          : "text-slate-700 hover:bg-slate-50"
      )}
    >
      {children}
    </button>
  );
}

export function V2OverflowMenuSeparator() {
  return <div className="my-1 border-t border-slate-200" role="separator" />;
}

export default function V2OverflowMenu({
  label = "Actions",
  buttonClassName = "",
  align = "right",
  children,
}) {
  const { tokens } = useV2Theme();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={v2cx(
          buttonClassName || tokens.button.secondary,
          "inline-flex items-center gap-1.5 whitespace-nowrap"
        )}
      >
        {label}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className={v2cx("h-4 w-4 transition-transform", open ? "rotate-180" : "")}
          aria-hidden
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      <div
        role="menu"
        className={v2cx(
          "absolute z-50 mt-1 w-56 rounded-lg border border-slate-200 bg-white py-1 shadow-lg",
          align === "left" ? "left-0" : "right-0",
          open ? "" : "hidden"
        )}
        onClick={(event) => {
          if (event.target.closest("[data-overflow-keep-open]")) return;
          setOpen(false);
        }}
      >
        {children}
      </div>
    </div>
  );
}
